import React from 'react';

export type PaymentMethodSlug = 'cash' | 'pix' | 'debit' | 'credit';

interface CounterOrderSummaryProps {
  total: number;
  itemCount: number;
  selectedPayment?: PaymentMethodSlug;
  setSelectedPayment?: (method: PaymentMethodSlug) => void;
  cashReceived: string;
  setCashReceived: (val: string) => void;
  customerNote: string;
  setCustomerNote: (val: string) => void;
  isProcessing: boolean;
  onFinalizeSale: () => void;
}

export const CounterOrderSummary: React.FC<CounterOrderSummaryProps> = ({
  total,
  itemCount,
  cashReceived,
  setCashReceived,
  customerNote,
  setCustomerNote,
  isProcessing,
  onFinalizeSale,
}) => {
  const cashReceivedNum = parseFloat(cashReceived.replace(',', '.')) || 0;
  const changeDue = Math.max(0, cashReceivedNum - total);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-4 sticky top-6">
      <div className="flex items-center justify-between pb-3 border-b border-slate-800">
        <div>
          <h2 className="text-xs font-bold text-white uppercase tracking-wider">
            Resumo do Pedido
          </h2>
          <p className="text-[11px] text-slate-400">Finalização da venda presencial</p>
        </div>
        <span className="bg-emerald-950/60 border border-emerald-800/60 text-emerald-400 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded flex items-center space-x-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
          <span>Balcão</span>
        </span>
      </div>

      {/* Customer Info (Optional) */}
      <div className="space-y-1">
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-between">
          <span>Identificação do Cliente</span>
          <span className="text-slate-600 lowercase font-normal">(opcional)</span>
        </label>
        <div className="relative">
          <i className="fas fa-user absolute left-3 top-1/2 -translate-y-1/2 text-slate-600 text-xs"></i>
          <input
            type="text"
            value={customerNote}
            onChange={e => setCustomerNote(e.target.value)}
            placeholder="Nome, CPF ou telefone do comprador..."
            className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500/60 rounded-lg pl-9 pr-3 py-1.5 text-xs text-white placeholder-slate-600 focus:outline-none"
          />
        </div>
      </div>

      {/* Forma de Pagamento: Sempre em Dinheiro */}
      <div className="p-3 bg-slate-950 rounded-lg border border-slate-800 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 rounded bg-emerald-950/60 border border-emerald-800/60 flex items-center justify-center text-emerald-400">
              <i className="fas fa-money-bill-wave text-xs"></i>
            </div>
            <div>
              <span className="text-xs font-bold text-white block leading-none">
                Pagamento em Dinheiro
              </span>
              <span className="text-[10px] text-slate-500">Venda presencial</span>
            </div>
          </div>
          <span className="text-[10px] text-emerald-400 font-semibold bg-emerald-950/50 border border-emerald-800/50 px-1.5 py-0.5 rounded">
            À vista
          </span>
        </div>

        {/* Cash change calculator */}
        <div className="pt-2 border-t border-slate-800/80 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-slate-400 font-medium">
              Valor Recebido:
            </span>
            <div className="flex items-center space-x-1">
              <span className="text-xs text-slate-500 font-bold">R$</span>
              <input
                type="text"
                value={cashReceived}
                onChange={e => setCashReceived(e.target.value)}
                placeholder="0,00"
                className="w-24 bg-slate-900 border border-slate-700 focus:border-emerald-500 rounded-md px-2 py-1 text-xs font-bold text-right text-white focus:outline-none"
              />
            </div>
          </div>

          {/* Quick Cash Buttons */}
          <div className="flex items-center space-x-1.5 overflow-x-auto pb-0.5">
            {[Math.ceil(total), 10, 20, 50, 100, 200]
              .filter(v => v >= Math.ceil(total) && v > 0)
              .slice(0, 4)
              .map(val => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setCashReceived(val.toString())}
                  className="px-2 py-0.5 bg-slate-800 hover:bg-slate-700 text-[10px] font-semibold text-slate-300 rounded transition-colors whitespace-nowrap"
                >
                  R$ {val}
                </button>
              ))}
          </div>

          {cashReceivedNum > 0 && (
            <div className="pt-1.5 border-t border-slate-800/80 flex items-center justify-between text-xs">
              <span className="text-slate-400 font-medium">Troco a devolver:</span>
              <span className={`font-bold text-sm ${cashReceivedNum >= total ? 'text-emerald-400' : 'text-amber-400'}`}>
                R$ {changeDue.toFixed(2).replace('.', ',')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Totals Breakdown */}
      <div className="space-y-2 pt-2 border-t border-slate-800 text-xs">
        <div className="flex justify-between text-slate-400">
          <span>Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'itens'})</span>
          <span className="font-semibold text-slate-300">R$ {total.toFixed(2).replace('.', ',')}</span>
        </div>
        <div className="flex justify-between text-slate-400">
          <span>Entrega / Retirada</span>
          <span className="text-emerald-400 font-semibold">Grátis (Balcão)</span>
        </div>
        <div className="flex justify-between text-base font-black text-white pt-2 border-t border-slate-800">
          <span>Total a Pagar</span>
          <span className="text-xl text-emerald-400">R$ {total.toFixed(2).replace('.', ',')}</span>
        </div>
      </div>

      {/* Checkout Button */}
      <button
        onClick={onFinalizeSale}
        disabled={isProcessing || total <= 0}
        className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-lg transition-colors flex items-center justify-center space-x-2 text-xs uppercase tracking-wider"
      >
        {isProcessing ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white"></div>
            <span>Registrando Venda...</span>
          </>
        ) : (
          <>
            <i className="fas fa-check-circle"></i>
            <span>Concluir Venda no Balcão • R$ {total.toFixed(2).replace('.', ',')}</span>
          </>
        )}
      </button>
    </div>
  );
};
